import React from 'react';
import { Box } from '@mui/material';
import { LessonExercise } from '../../../student/mockLessonContent';
import ChooseNextExerciseCard from './ChooseNextExerciseCard';
import ConversationExerciseCard from './ConversationExerciseCard';
import ImageExerciseCard from './ImageExerciseCard';
import ImageSelectSpeakExerciseCard from './ImageSelectSpeakExerciseCard';
import ListenRepeatExerciseCard from './ListenRepeatExerciseCard';
import MatchGameExerciseCard from './MatchGameExerciseCard';
import QuizExerciseCard from './QuizExerciseCard';
import QuizWordExerciseCard from './QuizWordExerciseCard';
import SpeakingExerciseCard from './SpeakingExerciseCard';
import SummaryExerciseCard from './SummaryExerciseCard';
import TypeSpeakExerciseCard from './TypeSpeakExerciseCard';
import WordIntroExerciseCard from './WordIntroExerciseCard';
import OpenResponseExerciseCard from './OpenResponseExerciseCard';
import { ExerciseLayout, ExerciseLayoutContext, ExerciseReady } from './exerciseLayout';

const exerciseNames: Record<LessonExercise['kind'], string> = {
    image: 'From your teacher',
    word: 'New word',
    listenRepeat: 'Listen and repeat',
    quiz: 'Quick check',
    quizWord: 'Character check',
    match: 'Matching game',
    imageSelectSpeak: 'Choose and speak',
    typeSpeak: 'Type and speak',
    speak: 'Speaking practice',
    conversation: 'Conversation practice',
    summary: 'Speaking summary',
    chooseNext: 'Choose what is next',
    openTextAudio: 'Write and speak',
    openAudio: 'Speak your answer',
    openText: 'Write your answer',
};

// Kept beside the shared renderer so mobile and desktop cannot diverge on which
// exercises need an action before Continue is enabled. Only the plain teacher
// card / picture and the summary have nothing to do; everything else waits for
// the student (listening to the word or every conversation line, answering,
// recording, matching or choosing).
export function defaultReadyFor(exercise: LessonExercise | undefined): boolean {
    if (exercise == null) {
        return true;
    }
    return exercise.kind === 'image' || exercise.kind === 'summary';
}

export function exerciseName(exercise: LessonExercise): string {
    if (exercise.kind === 'image') return exercise.emoji ? 'Look' : 'From your teacher';
    if (exercise.kind === 'quiz' && exercise.speakAfter) return 'Quiz & Speak';
    if (exercise.kind === 'quiz') return exercise.mode === 'audio' ? 'Listening check' : exercise.mode === 'picture' ? 'Picture check' : 'Quick check';
    if (exercise.kind === 'speak' && !exercise.graded) return 'Free speaking';
    if (exercise.kind === 'typeSpeak') return exercise.version === 2 ? 'Write your own, then say it' : 'Type, then say';
    if (exercise.kind === 'conversation') return { listen: 'Listen', listenNoCharacters: 'Listen only', listenSpeak: 'Listen, then speak', readSpeak: 'Read & Speak', readBlankSpeak: 'Fill the gaps out loud', speakOnly: 'From memory' }[exercise.mode];
    if (exercise.kind === 'openTextAudio' || exercise.kind === 'openAudio' || exercise.kind === 'openText') return exercise.title;
    return exerciseNames[exercise.kind];
}

export function renderLessonExercise(exercise: LessonExercise, onReady: ExerciseReady, layout: ExerciseLayout = 'mobile'): React.ReactNode {
    let card: React.ReactNode = null;
    switch (exercise.kind) {
        case 'image':
            card = <ImageExerciseCard exercise={exercise} />;
            break;
        case 'word':
            card = <WordIntroExerciseCard exercise={exercise} onReady={onReady} />;
            break;
        case 'listenRepeat':
            card = <ListenRepeatExerciseCard exercise={exercise} onReady={onReady} />;
            break;
        case 'quiz':
            card = <QuizExerciseCard exercise={exercise} onReady={onReady} />;
            break;
        case 'quizWord':
            card = <QuizWordExerciseCard exercise={exercise} onReady={onReady} />;
            break;
        case 'match':
            card = <MatchGameExerciseCard exercise={exercise} onReady={onReady} />;
            break;
        case 'imageSelectSpeak':
            card = <ImageSelectSpeakExerciseCard exercise={exercise} onReady={onReady} />;
            break;
        case 'typeSpeak':
            card = <TypeSpeakExerciseCard exercise={exercise} onReady={onReady} />;
            break;
        case 'speak':
            card = <SpeakingExerciseCard exercise={exercise} onReady={onReady} />;
            break;
        case 'conversation':
            card = <ConversationExerciseCard exercise={exercise} onReady={onReady} />;
            break;
        case 'summary':
            card = <SummaryExerciseCard exercise={exercise} />;
            break;
        case 'chooseNext':
            card = <ChooseNextExerciseCard exercise={exercise} onReady={onReady} />;
            break;
        case 'openTextAudio':
        case 'openAudio':
        case 'openText':
            card = <OpenResponseExerciseCard exercise={exercise} onReady={onReady} />;
            break;
    }
    return <ExerciseLayoutContext.Provider value={layout}>
        <Box sx={{ '@media (prefers-reduced-motion: reduce)': { '&, & *': { animation: 'none !important', transition: 'none !important' } } }}>
            {card}
        </Box>
    </ExerciseLayoutContext.Provider>;
}
